import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { OrganisationType } from "../../../../clients/graphqlTypes.js";
import type { OnboardingFlowStage } from "./OnboardingFlowModal.js";
import { OnboardingIntroIllustration } from "./OnboardingIllustrations.js";
import {
  ONBOARDING_BUTTON_HEIGHT,
  ONBOARDING_BUTTON_LABEL,
  ONBOARDING_COLORS,
} from "./onboardingDesign.js";
import { OnboardingStatusLabel } from "./OnboardingStatusLabel.js";

interface Props {
  organisationType: OrganisationType;
  setStage: (newStage: OnboardingFlowStage | null) => void;
}

/**
 * First screen after an organisation is created: confirms the account, says
 * what the setup covers and lets the user start it. Nothing is saved here, and
 * every later step has its own "Skip for now".
 */
export const OnboardingFlowIntroStage = ({
  organisationType,
  setStage,
}: Props) => {
  const { t } = useTranslate(["OrganisationProfile"]);

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        alignItems: "center",
        px: { xs: 2, md: "80px" },
        pt: { xs: 4, md: 0 },
        pb: { xs: 4, md: "40px" },
      }}
    >
      {/* `my: auto` centres the content, and unlike `justify-content: center`
          it never pushes the top out of reach when the screen is short. */}
      <Stack
        sx={{ width: "100%", maxWidth: 760, alignItems: "center", my: "auto" }}
      >
        <OnboardingIntroIllustration />
        <Box sx={{ mt: "20px" }}>
          <OnboardingStatusLabel>
            {t("Account created", { ns: "OrganisationProfile" })}
          </OnboardingStatusLabel>
        </Box>
        <Typography
          component="h1"
          sx={{
            mt: "16px",
            maxWidth: 760,
            textAlign: "center",
            fontSize: { xs: 30, md: 44 },
            fontWeight: 500,
            lineHeight: 1.1,
            letterSpacing: "-0.015em",
            color: ONBOARDING_COLORS.heading,
          }}
        >
          {t("Let’s get your Meister Systems ready", {
            ns: "OrganisationProfile",
          })}
        </Typography>
        <Typography
          sx={{
            mt: "18px",
            maxWidth: 680,
            textAlign: "center",
            fontSize: { xs: 17, md: 20 },
            lineHeight: 1.5,
            letterSpacing: "normal",
            color: ONBOARDING_COLORS.body,
          }}
        >
          {organisationType === "CRAFTSMAN"
            ? t(
                "Tell us what your company does and we'll set up your services for you.",
                { ns: "OrganisationProfile" }
              )
            : t(
                "Add your contact details and address, then start your first project.",
                { ns: "OrganisationProfile" }
              )}
        </Typography>
        <Stack spacing={1} sx={{ mt: "40px", alignItems: "center" }}>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            data-testid="start-onboarding"
            onClick={() => setStage("form")}
            sx={{
              height: ONBOARDING_BUTTON_HEIGHT,
              px: "22px",
              ...ONBOARDING_BUTTON_LABEL,
            }}
          >
            {t("Start setup", { ns: "OrganisationProfile" })}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
